/**
 * PWA 설치 유도 배너 (모바일 하단 sticky).
 *
 * 노출 조건은 lib/pwa/installPrompt.ts shouldShowInstallBanner() 참조.
 *
 * 영구지침: docs/develop/pwa/pwa-spec.md §3.5
 */
import { useEffect, useState } from 'react'
import { Smartphone, X } from 'lucide-react'
import {
  clearDeferredPrompt,
  getDeferredPrompt,
  markBannerDismissed,
  shouldShowInstallBanner,
} from '@/lib/pwa/installPrompt'
import { logPwaEvent } from '@/lib/pwa/installEvents'

export default function InstallBanner() {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const evaluate = () => {
      if (shouldShowInstallBanner()) {
        setVisible(true)
        logPwaEvent('BANNER_SHOWN')
      }
    }

    // 초기 평가
    evaluate()

    // beforeinstallprompt가 늦게 발생할 수 있음 → 이벤트 수신 시 재평가
    window.addEventListener('pwa:install-available', evaluate)

    // 설치 완료 시 자동 숨김
    const onInstalled = () => setVisible(false)
    window.addEventListener('pwa:installed', onInstalled)

    return () => {
      window.removeEventListener('pwa:install-available', evaluate)
      window.removeEventListener('pwa:installed', onInstalled)
    }
  }, [])

  const handleInstall = async () => {
    const prompt = getDeferredPrompt()
    if (!prompt) {
      setVisible(false)
      return
    }

    logPwaEvent('INSTALL_PROMPTED')
    await prompt.prompt()
    const choice = await prompt.userChoice

    if (choice.outcome === 'accepted') {
      logPwaEvent('INSTALLED')
    } else {
      logPwaEvent('BANNER_DISMISSED')
      markBannerDismissed()
    }
    clearDeferredPrompt()
    setVisible(false)
  }

  const handleDismiss = () => {
    markBannerDismissed()
    logPwaEvent('BANNER_DISMISSED')
    setVisible(false)
  }

  if (!visible) return null

  return (
    <div
      role="region"
      aria-label="앱 설치 안내"
      className="fixed inset-x-0 bottom-16 z-40 mx-auto max-w-md px-4"
    >
      <div className="flex items-start gap-3 rounded-2xl bg-white p-4 shadow-lg ring-1 ring-slate-200">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-blue-50">
          <Smartphone className="h-5 w-5 text-blue-600" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-slate-900">
            CareBalance를 홈 화면에 추가하세요
          </h2>
          <p className="mt-0.5 text-xs text-slate-600">
            아이콘 한 번으로 부모님 시설을 빠르게 찾을 수 있어요
          </p>
          <button
            type="button"
            onClick={handleInstall}
            className="mt-2 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            설치하기
          </button>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="닫기"
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
